<script lang="ts" setup>
import { ref, computed } from 'vue'
import customerService from '@/services/customer'
import { phoneFormatFilterInternational } from '@/utils/phone'
import DialogContentLayout from "@/components/common/DialogContentLayout.vue"

const verified = ref(false);
const submitting = ref(false);
const errorMessage = ref("");
const shake = ref(false);
const mfaCode = ref("");
const props = defineProps<{
  customerId: number;
  phone: string;
}>();

const emit = defineEmits(["confirm", "retry"]);

 const formattedPhone = computed(() => {
    if (!props.phone) {
      return ''
    }
    return phoneFormatFilterInternational(props.phone)
  })

const submit = async () => {
  submitting.value = true;
  try {
    await customerService.verifySMS(props.customerId, {
      phoneNumber: props.phone,
      code: mfaCode.value.toString(),
    });
    verified.value = true;
    setTimeout(() => {
      emit("confirm");
    }, 2000);
  } catch (err) {
    errorMessage.value =
      "Could not validate phone number. Please make sure your code is correct and try again.";
    console.error(err);
    shake.value = true;
    setTimeout(() => {
      shake.value = false;
    }, 1500);
  }
  submitting.value = false;
};
</script>

<template>
  <DialogContentLayout
  title="Verify Phone Number"
  >
      <div class="w-full padding-t-3 padding-x-6 padding-b-0 margin-t-8">
        <template v-if="!verified">
          <img
            src="@/assets/images/tripReceived.svg"
            class="display-block margin-x-auto max-w-232 margin-b-6"
          />
          <h3 class="font-22 font-bold text-center">Verify Phone Number</h3>
          <p class="text-center margin-b-6">
            Please enter the six digit code sent to {{ formattedPhone }}
          </p>
          <div
            class="d-flex align-center justify-center flex-column text-center"
          >
            <v-otp-input
              v-if="!submitting"
              id="reservation-detail-verify-phone-number"
              v-model="mfaCode"
              type="number"
              length="6"
              :class="{
                shake: shake,
              }"
              @finish="submit"
            />
            <v-progress-circular v-else indeterminate color="primary" />
            <p v-if="errorMessage" class="text-error font-medium font-14">
              {{ errorMessage }}
            </p>
            <div class="d-flex w-full justify-end">
              <v-btn
                plain
                small
                color="primary"
                class="padding-x-0 margin-t-3"
                @click="emit('retry')"
              >
                I didn't receive an SMS
              </v-btn>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="d-flex flex-column align-center justify-center w-full">
            <img
              src="@/assets/images/tripReceived.svg"
              class="display-block margin-x-auto max-w-232 margin-b-6"
            />
            <div class="d-flex align-center">
              <CUIcon
                key="ready-to-go-icon"
                color="success"
                view-box="0 0 24 24"
                class="w-24 h-24 margin-r-2"
              >
                check_circle_old
              </CUIcon>
              <h3
                class="font-bold"
                :class="$vuetify.breakpoint.xsOnly ? 'font-20' : 'font-22'"
              >
                Phone Number Confirmed!
              </h3>
            </div>

            <p class="text-center margin-t-4 margin-b-12">
              You're all set to receive SMS updates on the day of the trip.
            </p>
          </div>
        </template>
      </div>
  </DialogContentLayout>
</template>

<style lang="scss" scoped>
::v-deep .v-input.v-text-field.v-text-field--outlined {
  margin-top: 0 !important;
}
</style>
